
import * as R from 'ramda';
import { format } from 'date-fns';

import { getLocale } from './locale';

const STATUS_LOC = {
  OPEN: 'openingtimes.open',
  CLOSED: 'openingtimes.closed',
  BY_APPOINTMENT: 'openingtimes.by_appointment'
};

const timeFormat = { hour: '2-digit', minute: '2-digit' };
const regex24 = /^(00?|12 AM|24)$/;
const dateFormat = {
  en: { day: 'numeric', month: 'short', year: 'numeric' },
  any: { day: 'numeric', month: 'numeric', year: 'numeric' },
};

const getFormat = (formats, lang) => formats[lang] || formats.any;

const noYearFmt = lang => Object.assign(
  {}, getFormat(dateFormat, lang), { year: undefined }
);

export default class OpeningHoursFormatter {

  constructor (lang, translate) {
    this.locale = getLocale(lang);
    this.longDateFormat = new Intl.DateTimeFormat(
      lang, getFormat(dateFormat, lang)
    );
    this.shortDateFormat = new Intl.DateTimeFormat(
      lang, noYearFmt(lang)
    );
    this.timeFormat = new Intl.DateTimeFormat(lang, timeFormat);
    this.lang = lang;
    this.translate = translate;
    this.test24 = regex24.test.bind(regex24);
  }

  // weekdayShort :: Int -> String
  weekdayShort (day) {
    return this.locale.weekdayShort(day);
  }

  // daysRange :: String -> String
  daysRange (days) {
    const dayInts = days.split(',').map(d => parseInt(d, 10));
    const day = this.weekdayShort(dayInts[0]);
    const { length } = dayInts;
    return length == 1 ?
      day : `${day}&ndash;${this.weekdayShort(dayInts[length - 1])}`;
  }

  prettifyTime (hour, minute) {
    const date = new Date(2012, 11, 17, hour, minute);
    /*
      Bug https://bugzilla.mozilla.org/show_bug.cgi?id=1330307 makes hour returned
      by Intl.DateTimeFormat unusable on Fx (Windows). It should be fixed
      since version 67.
    */
    // const [/* h */, l1, m, l2 = '', p = ''] = this.timeFormat.formatToParts(date)
    //   .map(x => x.value);

    /* Use date-fns with custom locale formats */
    let [h, l1, m, l2 = '', p = ''] = format(date, this.locale.LT).split(/([\W])/);

    return [
      h,            // hour
      minute && l1, // separator like :
      minute && m,  // minute
      l2, p         // space + AM/PM or nothing
    ]
      .filter(x => !!x)
      .join('');
  }

  formatTime (date) {
    return format(date, this.locale.LT);
  }

  // formatDate :: String|Date -> String
  formatDate (str, inLongFormat) {
    if (!str) throw new Error(`Bad date '${str}'`);
    const date = new Date(str);
    const now = new Date();
    const isSameYear = date.getFullYear() == now.getFullYear();
    const fmt = (!inLongFormat && isSameYear) ? this.shortDateFormat : this.longDateFormat;
    try {
      return fmt.format(date);
    } catch (error) {
      console.error(error);
      return date.toLocaleDateString(this.lang);
    }
  }

  combineRange (from, to) {
    const prettify = [from, to].every(this.test24);
    return prettify ? '24h' : [from, to].join('&thinsp;&ndash;&thinsp;');
  }

  // formatDailyHours :: (String, String) -> String
  formatDailyHours ({from, to}) {
    const format = R.pipe(
      R.split(':'),
      R.map(x => parseInt(x, 10)),
      R.apply(this.prettifyTime.bind(this))
    );
    return this.combineRange(format(from), format(to));
  }

  formatExceptionHours ({ fromHour, fromMinute, toHour, toMinute }) {
    return this.combineRange(
      this.prettifyTime(fromHour, fromMinute),
      this.prettifyTime(toHour, toMinute)
    );
  }

  // statusText :: String -> String
  statusText (status) {
    return this.translate(STATUS_LOC[status]);
  }
}
